<template>
  <section
    class="successor-nomination"
    :aria-labelledby="headingId"
    data-successor-nomination
  >
    <h4 :id="headingId" class="successor-nomination__title">
      Nominate a new admin for {{ organisationName }}
    </h4>
    <p class="successor-nomination__prompt">
      {{ organisationName }} needs at least one admin. Choose who will manage
      it. You can choose more than one person.
    </p>

    <label :for="searchId" class="successor-nomination__search-label">
      Search members
    </label>
    <input
      :id="searchId"
      v-model="searchInput"
      class="unit-field smallInput successor-nomination__search"
      type="search"
      autocomplete="off"
      maxlength="80"
      placeholder="Search by name"
      :aria-controls="tableId"
    />

    <div class="successor-nomination__table-wrapper">
      <table :id="tableId" class="successor-nomination__table">
        <caption class="successor-nomination__caption">
          Members of {{ organisationName }} who can become admin
        </caption>
        <thead>
          <tr>
            <th scope="col" class="successor-nomination__select-column">
              Select
            </th>
            <th scope="col">Member</th>
            <th scope="col">Joined</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-if="suggestedSuccessor"
            class="successor-nomination__row successor-nomination__row--suggested"
            data-suggested-successor
          >
            <td>
              <input
                :id="checkboxId(suggestedSuccessor)"
                type="checkbox"
                :checked="isSelected(suggestedSuccessor)"
                :value="suggestedSuccessor.membershipRef"
                @change="toggle(suggestedSuccessor, $event.target.checked)"
              />
            </td>
            <td>
              <label
                :for="checkboxId(suggestedSuccessor)"
                class="successor-nomination__member"
              >
                <span class="successor-nomination__name">
                  {{ suggestedSuccessor.displayName }}
                </span>
                <Badge message="Suggested" tone="success" />
                <Badge
                  v-if="suggestedSuccessor.isAdmin"
                  message="Admin"
                  tone="neutral"
                />
                <small
                  v-if="suggestedSuccessor.publicIdentifier"
                  class="successor-nomination__handle"
                >
                  @{{ suggestedSuccessor.publicIdentifier }}
                </small>
                <small class="successor-nomination__hint">
                  Longest-standing member
                </small>
              </label>
            </td>
            <td class="successor-nomination__date">
              {{ formatJoined(suggestedSuccessor.joinedAt) }}
            </td>
          </tr>
          <tr
            v-for="candidate in visibleCandidates"
            :key="candidate.membershipRef"
            class="successor-nomination__row"
            data-successor-candidate
          >
            <td>
              <input
                :id="checkboxId(candidate)"
                type="checkbox"
                :checked="isSelected(candidate)"
                :value="candidate.membershipRef"
                @change="toggle(candidate, $event.target.checked)"
              />
            </td>
            <td>
              <label
                :for="checkboxId(candidate)"
                class="successor-nomination__member"
              >
                <span class="successor-nomination__name">
                  {{ candidate.displayName }}
                </span>
                <Badge v-if="candidate.isAdmin" message="Admin" tone="neutral" />
                <small
                  v-if="candidate.publicIdentifier"
                  class="successor-nomination__handle"
                >
                  @{{ candidate.publicIdentifier }}
                </small>
              </label>
            </td>
            <td class="successor-nomination__date">
              {{ formatJoined(candidate.joinedAt) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <LoadingState
      v-if="loading"
      variant="section"
      message="Loading members…"
    />
    <div
      v-else-if="errorMessage"
      class="successor-nomination__state"
      role="alert"
    >
      <p>{{ errorMessage }}</p>
      <UnitButton variant="secondary" size="small" @click="reloadPage">
        Retry
      </UnitButton>
    </div>
    <p
      v-else-if="visibleCandidates.length === 0"
      class="successor-nomination__state"
      role="status"
    >
      {{
        activeQuery
          ? `No members match "${activeQuery}".`
          : suggestedSuccessor
            ? "No other members to show."
            : "No members are available."
      }}
    </p>

    <nav
      v-if="pageIndex > 0 || hasMore"
      class="successor-nomination__pagination"
      :aria-label="`Member pages for ${organisationName}`"
    >
      <UnitButton
        variant="secondary"
        size="small"
        :disabled="loading || pageIndex === 0"
        data-successor-previous
        @click="previousPage"
      >
        Previous
      </UnitButton>
      <span class="successor-nomination__page">Page {{ pageIndex + 1 }}</span>
      <UnitButton
        variant="secondary"
        size="small"
        :disabled="loading || !hasMore"
        data-successor-next
        @click="nextPage"
      >
        Next
      </UnitButton>
    </nav>

    <p
      class="successor-nomination__summary"
      :class="{ 'successor-nomination__summary--missing': modelValue.length === 0 }"
      aria-live="polite"
      data-successor-summary
    >
      {{
        modelValue.length === 0
          ? "Choose at least one member."
          : `${modelValue.length} ${modelValue.length === 1 ? "member" : "members"} selected`
      }}
    </p>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import Badge from "@/components/GenericComponents/Badge.vue";
import LoadingState from "@/components/GenericComponents/LoadingState.vue";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";
import {
  accountAdministrationService,
  describeAccountAdministrationError,
} from "@/services/accountAdministrationService";

defineOptions({ name: "SuccessorNominationPanel" });

const SEARCH_DEBOUNCE_MS = 300;
let instanceCount = 0;

const props = defineProps({
  orgId: { type: String, required: true },
  organisationName: { type: String, required: true },
  subjectUid: { type: String, default: null },
  suggestedSuccessor: { type: Object, default: null },
  modelValue: { type: Array, default: () => [] },
});

const emit = defineEmits(["update:modelValue"]);

const instanceId = ++instanceCount;
const headingId = `successor-nomination-title-${instanceId}`;
const searchId = `successor-nomination-search-${instanceId}`;
const tableId = `successor-nomination-table-${instanceId}`;

const searchInput = ref("");
const activeQuery = ref("");
const candidates = ref([]);
const cursorStack = ref([null]);
const pageIndex = ref(0);
const nextCursor = ref(null);
const hasMore = ref(false);
const loading = ref(false);
const errorMessage = ref("");
let generation = 0;
let debounceTimer = null;

const visibleCandidates = computed(() =>
  candidates.value.filter(
    (candidate) =>
      candidate.membershipRef !== props.suggestedSuccessor?.membershipRef
  )
);

const joinedFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});
const formatJoined = (timestamp) =>
  Number.isFinite(timestamp) ? joinedFormatter.format(new Date(timestamp)) : "";

const checkboxId = (candidate) =>
  `successor-${instanceId}-${candidate.membershipRef}`;
const isSelected = (candidate) =>
  props.modelValue.includes(candidate.membershipRef);

const toggle = (candidate, checked) => {
  const without = props.modelValue.filter(
    (membershipRef) => membershipRef !== candidate.membershipRef
  );
  emit("update:modelValue", checked ? [...without, candidate.membershipRef] : without);
};

const loadPage = async () => {
  const requestGeneration = ++generation;
  loading.value = true;
  errorMessage.value = "";
  try {
    const page = await accountAdministrationService.listSuccessorCandidates({
      orgId: props.orgId,
      subjectUid: props.subjectUid,
      query: activeQuery.value,
      cursor: cursorStack.value[pageIndex.value],
    });
    if (requestGeneration !== generation) return;
    candidates.value = page.candidates;
    nextCursor.value = page.nextCursor;
    hasMore.value = page.hasMore;
  } catch (error) {
    if (requestGeneration !== generation) return;
    candidates.value = [];
    hasMore.value = false;
    errorMessage.value = describeAccountAdministrationError(error);
  } finally {
    if (requestGeneration === generation) loading.value = false;
  }
};

const resetAndLoad = () => {
  cursorStack.value = [null];
  pageIndex.value = 0;
  nextCursor.value = null;
  hasMore.value = false;
  return loadPage();
};

const reloadPage = () => loadPage();

const nextPage = () => {
  if (!hasMore.value || !nextCursor.value || loading.value) return;
  cursorStack.value = [
    ...cursorStack.value.slice(0, pageIndex.value + 1),
    nextCursor.value,
  ];
  pageIndex.value += 1;
  return loadPage();
};

const previousPage = () => {
  if (pageIndex.value === 0 || loading.value) return;
  pageIndex.value -= 1;
  cursorStack.value = cursorStack.value.slice(0, pageIndex.value + 1);
  return loadPage();
};

watch(searchInput, (value) => {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    const query = value.trim();
    if (query === activeQuery.value) return;
    activeQuery.value = query;
    resetAndLoad();
  }, SEARCH_DEBOUNCE_MS);
});

watch(
  () => [props.orgId, props.subjectUid],
  () => {
    candidates.value = [];
    resetAndLoad();
  }
);

onMounted(() => {
  const suggested = props.suggestedSuccessor?.membershipRef;
  if (suggested && !props.modelValue.includes(suggested)) {
    emit("update:modelValue", [suggested, ...props.modelValue]);
  }
  resetAndLoad();
});

onBeforeUnmount(() => {
  generation += 1;
  if (debounceTimer) clearTimeout(debounceTimer);
});
</script>

<style scoped>
.successor-nomination {
  display: grid;
  gap: var(--space-4);
  min-width: 0;
  padding: var(--space-5);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-sm);
  background: var(--color-surface-canvas);
  color: var(--color-text-primary);
}

.successor-nomination__title,
.successor-nomination__prompt,
.successor-nomination__state p,
.successor-nomination__summary {
  margin: 0;
}

.successor-nomination__title {
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-semibold);
  overflow-wrap: anywhere;
}

.successor-nomination__prompt,
.successor-nomination__hint,
.successor-nomination__handle,
.successor-nomination__page,
.successor-nomination__summary {
  color: var(--color-text-secondary);
}

.successor-nomination__prompt {
  font-size: var(--font-size-label);
}

.successor-nomination__search-label {
  justify-content: flex-start;
  margin: 0;
  font-size: var(--font-size-label);
  font-weight: var(--font-weight-medium);
  text-align: left;
}

.successor-nomination__search {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.successor-nomination__table-wrapper {
  max-width: 100%;
  overflow-x: auto;
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-sm);
}

.successor-nomination__table {
  width: 100%;
  margin: 0;
  border-collapse: collapse;
  font-size: var(--font-size-label);
}

.successor-nomination__caption {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.successor-nomination__table th,
.successor-nomination__table td {
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--color-border-subtle);
  text-align: left;
  vertical-align: middle;
}

.successor-nomination__table th {
  font-weight: var(--font-weight-semibold);
  background: var(--color-surface-raised);
}

.successor-nomination__table tbody tr:last-child td {
  border-bottom: 0;
}

.successor-nomination__select-column {
  width: var(--control-height-compact);
}

.successor-nomination__row--suggested {
  background: var(--color-surface-selected);
}

.successor-nomination__row input[type="checkbox"] {
  width: var(--control-height-micro);
  height: var(--control-height-micro);
  min-height: 0;
  margin: 0;
  accent-color: var(--color-accent-primary);
  cursor: pointer;
}

.successor-nomination__row input[type="checkbox"]:focus-visible {
  outline: var(--focus-ring-width) solid var(--focus-ring-color);
  outline-offset: var(--focus-ring-offset);
}

.successor-nomination__member {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;
  gap: var(--space-2);
  margin: 0;
  text-align: left;
  min-width: 0;
  cursor: pointer;
}

.successor-nomination__name {
  font-weight: var(--font-weight-medium);
  overflow-wrap: anywhere;
}

.successor-nomination__handle,
.successor-nomination__hint {
  flex-basis: 100%;
  overflow-wrap: anywhere;
}

.successor-nomination__date {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.successor-nomination__state {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-4);
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-label);
}

.successor-nomination__pagination {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-4);
}

.successor-nomination__page {
  font-size: var(--font-size-label);
}

.successor-nomination__summary {
  font-size: var(--font-size-label);
}

.successor-nomination__summary--missing {
  color: var(--color-feedback-danger-foreground);
}
</style>
